import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, ArrowRightLeft, Users, ChevronDown, ChevronRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const Transfers = () => {
  const [transfers, setTransfers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPlayers, setExpandedPlayers] = useState({});
  const [viewMode, setViewMode] = useState('player'); // 'player' or 'list'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [transfersRes, playersRes, clubsRes] = await Promise.all([
        api.get('/transfers'),
        api.get('/players'),
        api.get('/clubs')
      ]);
      setTransfers(transfersRes.data);
      setPlayers(playersRes.data);
      setClubs(clubsRes.data);
      
      // Auto-expand all players initially
      const expanded = {};
      playersRes.data.forEach(p => { expanded[p.id] = true; });
      setExpandedPlayers(expanded);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const statuses = [
    { value: 'interested', label: 'Interested', color: 'blue' },
    { value: 'negotiating', label: 'Negotiating', color: 'yellow' },
    { value: 'offer_made', label: 'Offer Made', color: 'orange' },
    { value: 'completed', label: 'Completed', color: 'green' },
    { value: 'cancelled', label: 'Cancelled', color: 'red' }
  ];

  const getStatusColor = (status) => {
    const statusObj = statuses.find(s => s.value === status);
    return statusObj?.color || 'gray';
  };

  const getStatusBadge = (status) => {
    const color = getStatusColor(status);
    const colorClasses = {
      green: 'bg-green-500/20 text-green-400',
      blue: 'bg-blue-500/20 text-blue-400',
      yellow: 'bg-white/20 text-white',
      orange: 'bg-orange-500/20 text-orange-400',
      red: 'bg-red-500/20 text-red-400',
      gray: 'bg-zinc-500/20 text-zinc-400'
    };
    return colorClasses[color] || colorClasses.gray;
  };

  // Get filtered transfers
  const filteredTransfers = useMemo(() => transfers.filter(t => {
    const matchesSearch = !searchTerm || 
      t.player_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.club_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
    return matchesSearch && matchesStatus;
  }), [transfers, searchTerm, statusFilter]);

  // Group transfers by player
  const playersWithTransfers = useMemo(() => {
    const transfersByPlayer = {};
    filteredTransfers.forEach(transfer => {
      if (!transfersByPlayer[transfer.player_id]) {
        transfersByPlayer[transfer.player_id] = {
          player: players.find(p => p.id === transfer.player_id) || { name: transfer.player_name, id: transfer.player_id },
          transfers: []
        };
      }
      transfersByPlayer[transfer.player_id].transfers.push({
        ...transfer,
        club: clubs.find(c => c.id === transfer.club_id)
      });
    });
    const statusPriority = { 'offer_made': 1, 'negotiating': 2, 'interested': 3, 'completed': 4, 'cancelled': 5 };
    Object.values(transfersByPlayer).forEach(group => {
      group.transfers.sort((a, b) => (statusPriority[a.status] || 99) - (statusPriority[b.status] || 99));
    });
    return Object.values(transfersByPlayer).sort((a, b) => a.player.name.localeCompare(b.player.name));
  }, [filteredTransfers, players, clubs]);

  const togglePlayer = (playerId) => {
    setExpandedPlayers(prev => ({
      ...prev,
      [playerId]: !prev[playerId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="relative bg-black/60 backdrop-blur-xl border-b border-white/5 overflow-hidden">
        <div className="hero-glow"></div>
        <div className="relative z-10 p-6 md:p-8 lg:p-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold uppercase tracking-tight leading-none text-white mb-2">
                Player Interest
              </h1>
              <p className="text-lg text-zinc-300">
                {playersWithTransfers.length} players • {filteredTransfers.length} total interests
              </p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-zinc-900/50 border border-white/10 rounded-sm overflow-hidden">
                <button
                  onClick={() => setViewMode('player')}
                  data-testid="view-mode-player"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'player' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span className="hidden sm:inline">Panels</span>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  data-testid="view-mode-list"
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-wide transition-all flex items-center gap-2 ${
                    viewMode === 'list' ? 'bg-white text-black' : 'text-white hover:bg-white/10'
                  }`}
                >
                  <ArrowRightLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">List</span>
                </button>
              </div>
              <Link
                to="/transfers/new"
                data-testid="add-new-transfer-button"
                className="inline-flex items-center gap-2 bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm px-6 py-3 transition-all duration-200"
              >
                <Plus className="w-5 h-5" strokeWidth={2} />
                Add Interest
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 md:p-8 lg:p-12">
        {/* Filters */}
        <div className="glass-card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
              <input
                type="text"
                placeholder="Search by player or club..."
                data-testid="search-transfers-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white placeholder:text-zinc-600 outline-none transition-all duration-200"
              />
            </div>
            <div className="relative">
              <ArrowRightLeft className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-500" strokeWidth={1.5} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="filter-status-select"
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-white focus:ring-1 focus:ring-white rounded-md pl-12 pr-4 py-3 text-white outline-none transition-all duration-200"
              >
                <option value="all">All Statuses</option>
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Content based on view mode */}
        {filteredTransfers.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <p className="text-zinc-500 text-lg">No transfers found. Add your first transfer to get started!</p>
          </div>
        ) : viewMode === 'player' ? (
          /* Player Panel View */
          <div className="space-y-6">
            {playersWithTransfers.map(({ player, transfers: playerTransfers }) => (
              <div 
                key={player.id} 
                className="glass-card overflow-hidden"
                data-testid={`player-panel-${player.id}`}
              >
                {/* Player Header */}
                <div 
                  onClick={() => togglePlayer(player.id)}
                  className="p-6 cursor-pointer hover:bg-white/5 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white/10 rounded-lg">
                      <Users className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">{player.name}</h2>
                      <p className="text-sm text-zinc-400">
                        {player.position && `${player.position} • `}
                        {player.current_club && `${player.current_club} • `}
                        {playerTransfers.length} interested club{playerTransfers.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Status summary badges */}
                    <div className="hidden md:flex gap-2">
                      {['offer_made', 'negotiating', 'interested'].map(status => {
                        const count = playerTransfers.filter(t => t.status === status).length;
                        if (count === 0) return null;
                        return (
                          <span 
                            key={status}
                            className={`px-2 py-1 rounded text-xs font-bold ${getStatusBadge(status)}`}
                          >
                            {count} {status.replace('_', ' ')}
                          </span>
                        );
                      })}
                    </div>
                    {expandedPlayers[player.id] ? (
                      <ChevronDown className="w-6 h-6 text-zinc-400" />
                    ) : (
                      <ChevronRight className="w-6 h-6 text-zinc-400" />
                    )}
                  </div>
                </div>

                {/* Interested Clubs */}
                {expandedPlayers[player.id] && (
                  <div className="border-t border-white/5">
                    <div className="p-4 bg-zinc-900/30">
                      <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">
                        Interested Clubs
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {playerTransfers.map((transfer) => (
                          <Link
                            key={transfer.id}
                            to={`/transfers/${transfer.id}`}
                            data-testid={`transfer-item-${transfer.id}`}
                            className="p-4 bg-black/30 border border-white/5 rounded-lg hover:border-[#FFFFFF]/30 transition-all group"
                          >
                            <div className="flex items-start gap-3">
                              {transfer.club?.badge_url ? (
                                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center overflow-hidden border border-white/10 flex-shrink-0">
                                  <img 
                                    src={transfer.club.badge_url} 
                                    alt={transfer.club_name}
                                    className="max-w-full max-h-full object-contain"
                                    onError={(e) => { 
                                      e.target.onerror = null; 
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              ) : (
                                <div className="p-2 bg-blue-500/10 rounded-lg flex-shrink-0">
                                  <Shield className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-bold text-white group-hover:text-white transition-colors truncate">
                                  {transfer.club_name}
                                </h4>
                                {transfer.club && (
                                  <p className="text-xs text-zinc-500 truncate">
                                    {transfer.club.league} • {transfer.club.country}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                              <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${getStatusBadge(transfer.status)}`}>
                                {transfer.status.replace('_', ' ')}
                              </span>
                              {transfer.transfer_fee && (
                                <span className="text-sm font-bold text-green-400">
                                  £{(transfer.transfer_fee / 1000000).toFixed(1)}M
                                </span>
                              )}
                            </div>
                            {transfer.transfer_date && (
                              <p className="mt-2 text-xs text-zinc-500">
                                Target: {transfer.transfer_date}
                              </p>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* List View (Original) */
          <div className="space-y-4">
            {[...filteredTransfers].sort((a, b) => (a.player_name || '').localeCompare(b.player_name || '')).map((transfer) => (
              <Link
                key={transfer.id}
                to={`/transfers/${transfer.id}`}
                data-testid={`transfer-item-${transfer.id}`}
                className="block glass-card p-6 card-hover"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="p-3 bg-white/10 rounded-lg">
                      <ArrowRightLeft className="w-6 h-6 text-white" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-white">{transfer.player_name}</h3>
                        <ArrowRightLeft className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
                        <h3 className="text-xl font-bold text-white">{transfer.club_name}</h3>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-zinc-400">
                        {transfer.transfer_date && (
                          <span>Date: {transfer.transfer_date}</span>
                        )}
                        {transfer.transfer_fee && (
                          <span className="text-green-400 font-bold">
                            Fee: £{(transfer.transfer_fee / 1000000).toFixed(1)}M
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <span className={`inline-block px-4 py-2 rounded text-xs font-bold uppercase ${getStatusBadge(transfer.status)}`}>
                      {transfer.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Transfers;
